import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import TopTopicsBlock from '../components/trending/TopTopicsBlock';
import { usePageSeo } from '../hooks/usePageSeo';
import {
  useArticleSections,
  articlesInSection,
  resolveSectionName,
  articleToTrendingItem,
  TOP_ARTICLES_N,
} from '../lib/articlesHierarchy';

// Section page (e.g. /articles/section/process) — the Articles counterpart of
// HowItWorksSectionPage: that section's top articles plus a "More Articles" link.
const ArticlesSectionPage = () => {
  const { sectionSlug } = useParams();
  const navigate = useNavigate();
  const { articles, loading } = useArticleSections();

  const sectionName = resolveSectionName(articles, sectionSlug);
  const sectionArticles = useMemo(() => articlesInSection(articles, sectionSlug), [articles, sectionSlug]);

  useEffect(() => {
    if (!loading && !sectionName) {
      navigate('/articles', { replace: true });
    }
  }, [loading, sectionName, navigate]);

  usePageSeo(
    sectionName ? `${sectionName} | Articles | AUTOXIUM` : undefined,
    sectionName ? `Explore ${sectionName} technical articles on AUTOXIUM.` : undefined
  );

  const items = useMemo(() => sectionArticles.map(articleToTrendingItem), [sectionArticles]);

  if (loading || !sectionName) {
    return (
      <div className="min-h-screen" data-testid="articles-section-loading">
        <div className="bg-chassis text-white py-16 md:py-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
            <Skeleton className="h-4 w-40 mb-6 bg-white/10" />
            <Skeleton className="h-10 w-1/2 bg-white/10" />
          </div>
        </div>
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="articles-section-page">
      <div className="bg-chassis text-white py-16 md:py-20">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-6"
            data-testid="back-to-articles"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Articles
          </Link>
          <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">Section</p>
          <h1 className="font-chivo text-3xl md:text-5xl font-black uppercase tracking-tighter">{sectionName}</h1>
        </div>
      </div>

      {sectionArticles.length === 0 ? (
        <div className="text-center py-16" data-testid="articles-section-empty">
          <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-chivo text-xl font-bold uppercase mb-2">No Articles Yet</h3>
          <p className="text-muted-foreground">Check back soon for new technical articles.</p>
        </div>
      ) : (
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <TopTopicsBlock
            sectionType="article"
            items={items}
            hasMore={sectionArticles.length > TOP_ARTICLES_N}
            moreTopicsPath={`/articles/section/${sectionSlug}/more`}
            topN={TOP_ARTICLES_N}
            title="Top Articles"
            moreLabel="More Articles"
          />
        </div>
      )}
    </div>
  );
};

export default ArticlesSectionPage;
